import React, { useState, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import Button from "ui/Button/Button";
import Link from "ui/Link/Link";
import Counter from "common/Counter/Counter";
import AddToCartModalWithPrices from "common/AddToCart/components/AddToCartModalWithPrices/AddToCartModalWithPrices";
import Price from "common/Price/Price";
import Badges from "common/Badges/Badges";
import BundleList from "common/ProductPreview/components/BundleList/BundleList";
import { useStore } from "store";
import { gtm, useOnWatch } from "helpers/analytics";
import { getImageSrcWithSize } from "helpers/image";
import {
  useBreakpoint,
  useUrl,
  useProductPrices,
  useLunaTrack,
} from "helpers/hooks";
import { cn } from "helpers/style";
import { IMAGE_SIZE_200_200 } from "constants/variables";
import styles from "./SearchProductPreview.module.scss";

type Props = {
  product: Entity.Product;
  onWatch?: (args?: any) => void;
  index?: number;
  analyticsMeta?: {
    position: number;
    list: string;
  };
  onGoToPage?: () => void;
  hideToast?: boolean;
  showExpiringPrices?: boolean;
};

const SearchProductPreview: React.FC<Props> = ({
  product,
  analyticsMeta,
  onWatch,
  index,
  onGoToPage,
  hideToast = false,
  showExpiringPrices,
}: Props) => {
  const { cart, user, notifications } = useStore();
  const productRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const prices = useProductPrices(product);
  const { getEntityLinkProps } = useUrl();
  const breakpoint = useBreakpoint();
  const { onImps, onTrack, hasImps } = useLunaTrack({
    product: product,
    analyticsMeta: analyticsMeta,
  });

  const price = useMemo(() => {
    if (user.isEmployeeMode) {
      return prices.sort((a, b) => a.price - b.price)[0];
    }

    return prices[0];
  }, [product, user.isEmployeeMode]);

  const productInCart = cart.get(product.id, price);
  const isAvailable =
    ((product.availableForOrder || product.availableForBooking) &&
      Boolean(price)) ||
    (price?.isExpirating && Boolean(price?.price) && showExpiringPrices);

  useOnWatch({
    subject: product,
    onWatch: hasImps ? onImps : onWatch,
    index,
    elementRef: productRef,
  });

  async function addToCart(e: React.MouseEvent) {
    e && e.stopPropagation();

    if (user.isEmployeeMode) {
      notifications.show({
        type: "modal",
        component: AddToCartModalWithPrices,
        props: {
          product,
        },
      });

      return;
    }

    gtm.autocompleteProductAdd();

    if (!submitting) {
      setSubmitting(true);
      try {
        await cart.add(product, price, 1, analyticsMeta, hideToast);
      } finally {
        setSubmitting(false);
      }
    }
  }

  async function handleChangeCount(value) {
    if (productInCart && !submitting) {
      setSubmitting(true);
      try {
        if (value === 0) {
          await cart.remove(product, price);
        } else {
          await cart.setItemCount(productInCart, value);
        }
      } finally {
        setSubmitting(false);
      }
    }
  }

  function stockNotification(e: React.MouseEvent) {
    e.stopPropagation();
  }

  /* todo: refactor this, remove form maybe */
  const description = [/*product.form,*/ product.instructions?.dosage].filter(
    Boolean
  );

  return (
    <div ref={productRef} className={styles.searchProductPreview}>
      <Link {...getEntityLinkProps("product", product)}>
        <span
          className={styles.searchProductPreviewBackground}
          onClick={e => {
            onTrack(e);
            onGoToPage && onGoToPage();
          }}
          aria-label={`перейти на страницу ${product.name}`}
          aria-labelledby={`перейти на страницу ${product.name}`}
        />
      </Link>
      <div
        className={cn([
          styles.searchProductPreviewImageContainer,
          !product.image &&
            styles.searchProductPreviewImageContainerWithoutImage,
        ])}
      >
        {product.image && (
          <img
            src={getImageSrcWithSize(product.image, IMAGE_SIZE_200_200)}
            alt={product.name}
          />
        )}
      </div>

      <div className={styles.searchProductPreviewInner}>
        <div className={styles.searchProductPreviewLeft}>
          <div className={styles.searchProductPreviewInfo}>
            {product.badges && product.badges.length > 0 && (
              <Badges
                badges={product.badges}
                tooltipProps={{
                  maxWidth: 292,
                  placement: "top-start",
                }}
                advertisementTooltipProps={{
                  maxWidth: 360,
                }}
                alt="Шильдик акции"
                badgeProps={{ className: styles.searchProductPreviewInfoBadge }}
              />
            )}
            <p className={styles.searchProductPreviewTitle}>{product.name}</p>
            {description.length > 0 && (
              <p className={styles.searchProductPreviewSubtitle}>
                {description.map((item, index) => (
                  <span key={index}>{item}</span>
                ))}
              </p>
            )}

            {product.isBundle && (
              <BundleList
                className={styles.searchProductPreviewBundleList}
                items={product.bundleItemsSimple}
                link={getEntityLinkProps("product", product)}
                wrapped
              />
            )}
          </div>
          <div className={styles.searchProductPreviewPriceContainer}>
            {isAvailable ? (
              <Price
                className={styles.searchProductPreviewPriceContainerCommonPrice}
                hasOnlyBookingPrices={
                  product.hasOnlyBookingPrices || user.isEmployeeMode
                }
                price={price.price * (productInCart?.quantity || 1)}
                priceOld={price.priceOld}
                discount={price.discount}
                bonuses={!product.hasOnlyBookingPrices}
                small
                smallBonuses
              />
            ) : (
              "Нет в наличии"
            )}
          </div>
        </div>

        <div className={styles.searchProductPreviewActionsContainer}>
          <div className={styles.searchProductPreviewPriceContainerMobile}>
            {isAvailable ? (
              <Price
                className={styles.searchProductPreviewPriceContainerCommonPrice}
                hasOnlyBookingPrices={
                  product.hasOnlyBookingPrices || user.isEmployeeMode
                }
                price={price.price * (productInCart?.quantity || 1)}
                priceOld={price.priceOld}
                discount={price.discount}
                bonuses={!product.hasOnlyBookingPrices}
                small
                smallBonuses
              />
            ) : (
              "Нет в наличии"
            )}
          </div>

          {/*<BuyBySubscription>*/}
          {/*  Купить по подписке <br />*/}
          {/*  <nobr>*/}
          {/*    за <span>328 ₽ / мес.</span>{" "}*/}
          {/*  </nobr>*/}
          {/*  <div>*/}
          {/*    <Badge color="blue" small>*/}
          {/*      -15%*/}
          {/*    </Badge>*/}
          {/*  </div>*/}
          {/*</BuyBySubscription>*/}

          <div className={styles.searchProductPreviewActions}>
            {isAvailable &&
              (!productInCart || user.isEmployeeMode ? (
                <Button
                  className={styles.searchProductPreviewActionsButton}
                  disabled={submitting}
                  onClick={product.price ? addToCart : stockNotification}
                  orange
                >
                  В корзину
                </Button>
              ) : (
                <div
                  className={styles.searchProductPreviewCounterContainer}
                  onClick={e => e.stopPropagation()}
                >
                  <Counter
                    size={breakpoint === "mobile" ? "small" : "large"}
                    disabled={submitting}
                    value={productInCart?.quantity || 1}
                    onChange={handleChangeCount}
                    min={0}
                    max={price.maxQuantity}
                  />
                </div>
              ))}
            {/*<BuyInOneClick>Купить в 1 клик</BuyInOneClick>*/}
          </div>
        </div>

        {product.isAdv && product.warning && (
          <div className={styles.searchProductPreviewLabel}>
            {product.warning}
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(SearchProductPreview);
